<template>
  <div class="page">
    <h1>Einkaufsliste</h1>

    <!-- Aktive Liste + Verwaltung -->
    <div class="listen-leiste">
      <label class="listen-label" for="listen-auswahl">Aktive Liste</label>
      <div class="listen-zeile">
        <div class="input-field listen-select-feld">
          <font-awesome-icon icon="fa-solid fa-list" />
          <select id="listen-auswahl" :value="AktiveEinkaufsliste.Id" @change="ListeWechseln($event)">
            <option v-for="Liste in Einkaufslisten" :key="Liste.Id" :value="Liste.Id">{{ Liste.Name }}</option>
          </select>
        </div>
        <button type="button" class="listen-aktion" title="Neue Einkaufsliste" @click="NeueListeOeffnen">
          <font-awesome-icon icon="fa-solid fa-plus" />
        </button>
        <button type="button" class="listen-aktion" title="Liste umbenennen" @click="UmbenennenOeffnen">
          <font-awesome-icon icon="fa-solid fa-pen" />
        </button>
        <button
          type="button"
          class="listen-aktion listen-aktion--rot"
          title="Liste löschen"
          :disabled="Einkaufslisten.length <= 1"
          @click="ListeLoeschen"
        >
          <font-awesome-icon icon="fa-solid fa-trash" />
        </button>
      </div>
    </div>

    <p v-if="Fehlermeldung" class="error-message">{{ Fehlermeldung }}</p>
    <p v-if="Hinweis" class="hint-message success">{{ Hinweis }}</p>

    <div class="hub-grid">
      <button type="button" class="hub-karte hub-karte--button" @click="PlanungOeffnen">
        <span class="hub-icon" :style="{ background: Tint('#C79A2A'), color: '#C79A2A' }">
          <font-awesome-icon icon="fa-solid fa-calendar-plus" />
        </span>
        <span class="hub-text">
          <span class="hub-titel">Einkaufsliste aus Planung</span>
          <span class="hub-untertitel">Zutaten geplanter Tage übernehmen</span>
        </span>
        <font-awesome-icon icon="fa-solid fa-chevron-right" class="hub-pfeil" />
      </button>

      <router-link
        v-for="Karte in Karten"
        :key="Karte.Pfad"
        :to="Karte.Pfad"
        class="hub-karte"
      >
        <span class="hub-icon" :style="{ background: Tint(Karte.Farbe), color: Karte.Farbe }">
          <font-awesome-icon :icon="Karte.Icon" />
        </span>
        <span class="hub-text">
          <span class="hub-titel">{{ Karte.Titel }}</span>
          <span class="hub-untertitel">{{ Karte.Untertitel }}</span>
        </span>
        <font-awesome-icon icon="fa-solid fa-chevron-right" class="hub-pfeil" />
      </router-link>
    </div>

    <!-- Neue Liste / Umbenennen -->
    <Teleport to="body">
      <div v-if="NamensDialogOffen" class="overlay" @click.self="NamensDialogSchliessen">
        <div class="bottom-sheet" role="dialog" aria-modal="true">
          <div class="sheet-griff"></div>
          <h3 class="sheet-titel">{{ NamensDialogModus === 'neu' ? 'Neue Einkaufsliste' : 'Liste umbenennen' }}</h3>
          <div class="input-field">
            <font-awesome-icon icon="fa-solid fa-list" />
            <input
              ref="NamensFeld"
              v-model="NamensEingabe"
              type="text"
              placeholder="Name der Liste"
              @keyup.enter="NamensDialogSpeichern"
            />
          </div>
          <p v-if="NamensDialogFehler" class="error-message">{{ NamensDialogFehler }}</p>
          <button type="button" class="btn btn-primary" @click="NamensDialogSpeichern">
            {{ NamensDialogModus === 'neu' ? 'Liste anlegen' : 'Speichern' }}
          </button>
          <button type="button" class="btn btn-secondary" @click="NamensDialogSchliessen">Abbrechen</button>
        </div>
      </div>
    </Teleport>

    <!-- Einkaufsliste aus Planung -->
    <Teleport to="body">
      <div v-if="PlanungOffen" class="overlay" @click.self="PlanungSchliessen">
        <div class="bottom-sheet" role="dialog" aria-modal="true" aria-label="Einkaufsliste aus Planung">
          <div class="sheet-griff"></div>
          <h3 class="sheet-titel">Einkaufsliste aus Planung</h3>
          <p class="planung-hinweis">Wähle die Tage, deren Zutaten auf die Einkaufsliste sollen. Punkte markieren Tage mit geplanten Gerichten.</p>

          <div class="wochen-liste">
            <div v-for="Woche in PlanungsWochen" :key="Woche.Titel" class="woche-block">
              <span class="woche-titel">{{ Woche.Titel }}</span>
              <div class="tage-grid">
                <button
                  v-for="Tag in Woche.Tage"
                  :key="Tag.Datum"
                  type="button"
                  class="tag-kachel"
                  :class="{
                    'tag-kachel--gewaehlt': GewaehlteTage.includes(Tag.Datum),
                    'tag-kachel--heute': Tag.IstHeute
                  }"
                  @click="TagToggeln(Tag.Datum)"
                >
                  <span class="tag-kachel-name">{{ Tag.Wochentag }}</span>
                  <span class="tag-kachel-datum">{{ Tag.Anzeige }}</span>
                  <span class="tag-kachel-punkt" :class="{ 'tag-kachel-punkt--aktiv': Tag.HatPlanung }"></span>
                </button>
              </div>
            </div>
          </div>

          <label class="listen-label" for="planung-ziel">Auf welche Einkaufsliste?</label>
          <div class="input-field">
            <font-awesome-icon icon="fa-solid fa-cart-shopping" />
            <select id="planung-ziel" v-model.number="PlanungsZielListe">
              <option v-for="Liste in Einkaufslisten" :key="Liste.Id" :value="Liste.Id">{{ Liste.Name }}</option>
            </select>
          </div>

          <p v-if="PlanungFehler" class="error-message">{{ PlanungFehler }}</p>

          <button
            type="button"
            class="btn btn-primary"
            :disabled="GewaehlteTage.length === 0 || PlanungLaeuft"
            @click="PlanungUebernehmen"
          >
            <span class="btn-inhalt">
              <font-awesome-icon icon="fa-solid fa-cart-plus" />
              Zutaten übernehmen ({{ GewaehlteTage.length }} {{ GewaehlteTage.length === 1 ? 'Tag' : 'Tage' }})
            </span>
          </button>
          <button type="button" class="btn btn-secondary" @click="PlanungSchliessen">Abbrechen</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  Einkaufslisten,
  EinkaufslistenLaden,
  AktiveEinkaufsliste,
  AktiveEinkaufslisteSetzen,
  EinkaufslisteAnlegen,
  EinkaufslisteUmbenennen,
  EinkaufslisteEntfernen,
  EinkaufslisteFinden,
  EinkaufslisteAusPlanungUebernehmen,
  EinkaufslisteLaden
} from '../composables/useZutaten'
import { Wochenplanpositionen, WochenplanungLaden } from '../composables/useWochenplanung'

export default {
  name: 'EinkaufslisteView',
  data() {
    return {
      Einkaufslisten,
      AktiveEinkaufsliste,
      Wochenplanpositionen,
      Fehlermeldung: '',
      Hinweis: '',
      HinweisTimer: null,
      NamensDialogOffen: false,
      NamensDialogModus: 'neu',
      NamensEingabe: '',
      NamensDialogFehler: '',
      PlanungOffen: false,
      GewaehlteTage: [],
      PlanungsZielListe: null,
      PlanungFehler: '',
      PlanungLaeuft: false,
      Karten: [
        { Pfad: '/einkaufsliste/anzeigen',            Icon: 'fa-solid fa-list-check',   Farbe: '#3DA568', Titel: 'Einkaufsliste',        Untertitel: 'Offene Einkäufe abhaken' },
        { Pfad: '/einkaufsliste/gekauft',             Icon: 'fa-solid fa-square-check', Farbe: '#4A7DDE', Titel: 'Gekaufte Zutaten',     Untertitel: 'Bereits erledigte Einkäufe' },
        { Pfad: '/einkaufsliste/zutaten-anzeigen',    Icon: 'fa-solid fa-carrot',       Farbe: '#E07B2A', Titel: 'Zutaten',              Untertitel: 'Dein Zutaten-Katalog' },
        { Pfad: '/einkaufsliste/zutaten-hinzufuegen', Icon: 'fa-solid fa-plus',         Farbe: '#8B5CF6', Titel: 'Zutat hinzufügen',     Untertitel: 'Neue Zutat anlegen' },
        { Pfad: '/einkaufsliste/kategorien',          Icon: 'fa-solid fa-tags',         Farbe: '#D9548C', Titel: 'Kategorien',           Untertitel: 'Verwalten & sortieren' }
      ]
    }
  },
  computed: {
    PlanungsWochen() {
      // Montag der aktuellen Woche bestimmen (ISO: Montag = Wochenstart)
      const Heute = new Date()
      Heute.setHours(0, 0, 0, 0)
      const MontagAktuell = new Date(Heute)
      MontagAktuell.setDate(Heute.getDate() - ((Heute.getDay() + 6) % 7))

      const GeplanteTage = new Set(this.Wochenplanpositionen.map(p => p.Datum))
      const Wochentage = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

      return [
        { Titel: 'Vorherige Woche', Start: -7 },
        { Titel: 'Aktuelle Woche', Start: 0 },
        { Titel: 'Kommende Woche', Start: 7 }
      ].map(Woche => ({
        Titel: Woche.Titel,
        Tage: Array.from({ length: 7 }, (Unbenutzt, Index) => {
          const Datum = new Date(MontagAktuell)
          Datum.setDate(MontagAktuell.getDate() + Woche.Start + Index)
          const Iso = `${Datum.getFullYear()}-${String(Datum.getMonth() + 1).padStart(2, '0')}-${String(Datum.getDate()).padStart(2, '0')}`
          return {
            Datum: Iso,
            Wochentag: Wochentage[Index],
            Anzeige: `${Datum.getDate()}.${Datum.getMonth() + 1}.`,
            HatPlanung: GeplanteTage.has(Iso),
            IstHeute: Datum.getTime() === Heute.getTime()
          }
        })
      }))
    }
  },
  async created() {
    await EinkaufslistenLaden()
  },
  methods: {
    Tint(Farbe) {
      return `color-mix(in srgb, ${Farbe} 15%, transparent)`
    },
    HinweisZeigen(Text) {
      this.Hinweis = Text
      clearTimeout(this.HinweisTimer)
      this.HinweisTimer = setTimeout(() => { this.Hinweis = '' }, 3000)
    },
    ListeWechseln(Ereignis) {
      AktiveEinkaufslisteSetzen(Number(Ereignis.target.value))
    },
    NeueListeOeffnen() {
      this.NamensDialogModus = 'neu'
      this.NamensEingabe = ''
      this.NamensDialogFehler = ''
      this.NamensDialogOffen = true
      this.$nextTick(() => this.$refs.NamensFeld?.focus())
    },
    UmbenennenOeffnen() {
      this.NamensDialogModus = 'umbenennen'
      this.NamensEingabe = EinkaufslisteFinden(this.AktiveEinkaufsliste.Id)?.Name || ''
      this.NamensDialogFehler = ''
      this.NamensDialogOffen = true
      this.$nextTick(() => this.$refs.NamensFeld?.focus())
    },
    NamensDialogSchliessen() {
      this.NamensDialogOffen = false
    },
    async NamensDialogSpeichern() {
      this.NamensDialogFehler = ''
      const Name = this.NamensEingabe.trim()
      if (!Name) {
        this.NamensDialogFehler = 'Name darf nicht leer sein.'
        return
      }
      try {
        if (this.NamensDialogModus === 'neu') {
          const Neu = await EinkaufslisteAnlegen(Name)
          AktiveEinkaufslisteSetzen(Neu.Id)
          this.HinweisZeigen(`Liste „${Name}" angelegt und ausgewählt.`)
        } else {
          await EinkaufslisteUmbenennen(this.AktiveEinkaufsliste.Id, Name)
        }
        this.NamensDialogOffen = false
      } catch (Fehler) {
        this.NamensDialogFehler = Fehler.message
      }
    },
    async ListeLoeschen() {
      this.Fehlermeldung = ''
      const Liste = EinkaufslisteFinden(this.AktiveEinkaufsliste.Id)
      if (!Liste) return
      if (!confirm(`Liste „${Liste.Name}" samt allen Einträgen wirklich löschen?`)) return
      try {
        await EinkaufslisteEntfernen(Liste.Id)
      } catch (Fehler) {
        this.Fehlermeldung = Fehler.message
      }
    },
    async PlanungOeffnen() {
      this.GewaehlteTage = []
      this.PlanungFehler = ''
      this.PlanungsZielListe = this.AktiveEinkaufsliste.Id
      this.PlanungOffen = true
      await WochenplanungLaden()
    },
    PlanungSchliessen() {
      this.PlanungOffen = false
    },
    TagToggeln(Datum) {
      const Index = this.GewaehlteTage.indexOf(Datum)
      if (Index === -1) {
        this.GewaehlteTage.push(Datum)
      } else {
        this.GewaehlteTage.splice(Index, 1)
      }
    },
    async PlanungUebernehmen() {
      this.PlanungFehler = ''
      this.PlanungLaeuft = true
      try {
        // Positionen einmal laden, damit die Rueckgabe sauber in den lokalen Zustand gemerged wird
        await EinkaufslisteLaden()
        const Ergebnis = await EinkaufslisteAusPlanungUebernehmen(this.GewaehlteTage, this.PlanungsZielListe)
        this.PlanungOffen = false
        if (Ergebnis.AnzahlZutaten > 0) {
          const ListenName = EinkaufslisteFinden(this.PlanungsZielListe)?.Name || 'Einkaufsliste'
          this.HinweisZeigen(`${Ergebnis.AnzahlZutaten} Zutat${Ergebnis.AnzahlZutaten === 1 ? '' : 'en'} auf „${ListenName}" gesetzt.`)
        } else {
          this.HinweisZeigen(Ergebnis.Hinweis || 'Für die gewählten Tage sind keine Gerichte geplant.')
        }
      } catch (Fehler) {
        this.PlanungFehler = Fehler.message
      } finally {
        this.PlanungLaeuft = false
      }
    }
  }
}
</script>

<style scoped>
/* ── Listen-Verwaltung ───────────────────────────────── */
.listen-leiste {
  margin: 12px 0 16px;
  max-width: 480px;
}

.listen-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text-muted);
  margin-bottom: 6px;
}

.listen-zeile {
  display: flex;
  align-items: center;
  gap: 8px;
}

.listen-select-feld {
  flex: 1;
  min-width: 0;
}

.listen-select-feld select {
  flex: 1;
  border: none;
  background-color: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 1rem;
  outline: none;
  min-width: 0;
}

.listen-aktion {
  width: 40px;
  height: 40px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-bg-alt);
  color: var(--color-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  transition: border-color 0.2s ease, color 0.2s ease;
}

.listen-aktion:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.listen-aktion--rot:hover:not(:disabled) {
  border-color: var(--color-danger);
  color: var(--color-danger);
}

.listen-aktion:disabled {
  opacity: 0.35;
  cursor: default;
}

.hub-karte--button {
  border: none;
  font-family: inherit;
  font-size: inherit;
  text-align: left;
  cursor: pointer;
  width: 100%;
}

/* ── Bottom Sheets ───────────────────────────────────── */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 16, 0.45);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-end;
  z-index: 200;
  animation: Einblenden 0.25s ease both;
}

.bottom-sheet {
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding: 12px 16px 32px;
  width: 100%;
  max-height: 88vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: SheetHoch 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@media (min-width: 768px) {
  .overlay {
    justify-content: center;
    align-items: center;
  }
  .bottom-sheet {
    border-radius: var(--radius-xl);
    max-width: 520px;
    max-height: 84vh;
    padding: 24px;
  }
  .sheet-griff { display: none; }
}

.sheet-griff {
  width: 40px;
  height: 4px;
  background: var(--color-border);
  border-radius: 2px;
  margin: 0 auto 4px;
  flex-shrink: 0;
}

.sheet-titel {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.bottom-sheet .input-field select {
  flex: 1;
  border: none;
  background-color: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 1rem;
  outline: none;
}

/* ── Tages-Auswahl ───────────────────────────────────── */
.planung-hinweis {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.wochen-liste {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.woche-titel {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-muted);
}

.tage-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-top: 6px;
}

.tag-kachel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 2px 6px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-bg-alt);
  color: var(--color-text);
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.tag-kachel:hover {
  border-color: var(--color-primary);
}

.tag-kachel--heute {
  border-style: dashed;
}

.tag-kachel--gewaehlt {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #fff;
}

.tag-kachel-name {
  font-size: 0.75rem;
  font-weight: 700;
}

.tag-kachel-datum {
  font-size: 0.7rem;
  opacity: 0.8;
  font-variant-numeric: tabular-nums;
}

.tag-kachel-punkt {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: transparent;
}

.tag-kachel-punkt--aktiv {
  background: var(--color-accent, #C79A2A);
}

.tag-kachel--gewaehlt .tag-kachel-punkt--aktiv {
  background: #fff;
}
</style>
